import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { post, C, THUMB_BASE, LOGO_BASE } from "./utils";
import { ThemeCtx, Icon, SORT_OPTIONS } from "./AppShell";
import { GalleryTile } from "./Dashboard";
import { BulkActionBar, SelectableTile, ActionMsg, ConfirmDialog, useFilterActions } from "./FilterActions";

// ── Helpers — identical to Dashboard ─────────────────────────────────────────
const since7days  = () => Math.floor((Date.now() - 7*24*60*60*1000) / 1000);
const PAGE_SIZE   = 20;
const isImage     = (p="") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const fileName    = (p="") => p.split("/").pop();
const stripHtml   = (h="") => h.replace(/<[^>]*>/g," ").replace(/&nbsp;/g," ").replace(/\s+/g," ").trim();
const getPrimaryProduct    = (arr=[]) => arr.find(p=>(p.PRIMARY==="1"||p.PRIMARY===1)&&String(p.BRANDID)!=="0")?.BRANDNAME||"";
const getPrimaryIndication = (arr=[]) => arr.find(i=>(i.PRIMARY==="1"||i.PRIMARY===1)&&String(i.INDICATIONID)!=="0")?.INDICATIONNAME||"";
const getCompanyName = (c) => (c&&String(c.SPONSORID)!=="0")?c.SPONSORNAME||"":"";
const getCompanyLogo = (c) => (c&&String(c.SPONSORID)!=="0"&&c.COMPANY_LOGO)?LOGO_BASE+c.COMPANY_LOGO:null;

const openDocTab = (doc, subId) => {
  const base = window.location.origin + window.location.pathname;
  localStorage.setItem(`doc_stateid_${doc.DOCUMENTID}`, "1");
  window.open(`${base}?fdetail=1&doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID}&sub=${subId||""}`, "_blank", "noopener,noreferrer");
};

// ── Identical to Dashboard StatRow ────────────────────────────────────────────
const StatRow = ({fav,attach,likes,imgCount,comments}) => (
  <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
    {fav&&<Icon name="star" size={11} color="#EF9F27"/>}
    {[{n:"attach",v:attach},{n:"like",v:likes},{n:"image",v:imgCount},{n:"chat",v:comments}].map(s=>(
      <div key={s.n} style={{display:"flex",alignItems:"center",gap:"2px",fontSize:"12px",color:C.hint}}>
        <Icon name={s.n} size={11} color={C.hint}/><span>{s.v}</span>
      </div>
    ))}
  </div>
);

// ── Identical to Dashboard Spinner ────────────────────────────────────────────
const Spinner = () => (
  <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",padding:"2rem",color:C.muted,fontSize:"14px"}}>
    <div style={{width:"16px",height:"16px",border:`2px solid ${C.border}`,borderTopColor:C.primary,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
    Loading...
    <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
  </div>
);

const LoadMore = ({onClick}) => (
  <div style={{textAlign:"center",marginTop:"1.5rem"}}>
    <button onClick={onClick} style={{padding:"9px 28px",borderRadius:"8px",border:`1px solid ${C.border}`,background:"#fff",color:C.primary,fontSize:"14px",fontWeight:"500",cursor:"pointer"}}>
      Load more
    </button>
  </div>
);

// ── Drag-scroll identical to Dashboard ───────────────────────────────────────
function useDragScroll() {
  const ref=useRef(null); const drag=useRef({on:false,x:0,sx:0});
  useEffect(()=>{
    const el=ref.current; if(!el) return;
    const down=e=>{drag.current={on:true,x:e.clientX,sx:el.scrollLeft};el.style.cursor="grabbing";el.style.userSelect="none";};
    const move=e=>{if(!drag.current.on)return;el.scrollLeft=drag.current.sx-(e.clientX-drag.current.x);};
    const up=()=>{drag.current.on=false;el.style.cursor="grab";el.style.userSelect="";};
    el.addEventListener("mousedown",down);el.addEventListener("mousemove",move);el.addEventListener("mouseup",up);el.addEventListener("mouseleave",up);
    return()=>{el.removeEventListener("mousedown",down);el.removeEventListener("mousemove",move);el.removeEventListener("mouseup",up);el.removeEventListener("mouseleave",up);};
  });
  return ref;
}

// ── HashtagBar identical to Dashboard ────────────────────────────────────────
function HashtagBar({hashtags,active,onChange}) {
  const ref=useDragScroll();
  if(!hashtags.length) return null;
  return(
    <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"12px"}}>
      <div ref={ref} style={{display:"flex",gap:"6px",overflowX:"auto",scrollbarWidth:"none",cursor:"grab",paddingBottom:"2px"}}>
        {["All",...hashtags].map(tag=>{
          const sel=tag==="All"?!active:active===tag;
          return(
            <button key={tag} onClick={()=>onChange(tag==="All"?null:tag)}
              style={{flexShrink:0,padding:"4px 12px",borderRadius:"20px",border:`1px solid ${sel?C.primary:C.border}`,
                background:sel?C.primary:"#fff",color:sel?"#fff":C.muted,
                fontSize:"12px",fontWeight:sel?"600":"400",cursor:"pointer",transition:"all .12s"}}>
              {tag}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── ScreenHeader identical to Dashboard ──────────────────────────────────────
function ScreenHeader({title,subtitle,searchInput,onSearch,sort,onSort,placeholder}) {
  const isMobile = useContext(ThemeCtx)?.isMobile || false;
  return(
    <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"16px"}}>
      <div>
        <div style={{fontSize:"19px",fontWeight:"500",color:C.text}}>{title}</div>
        <div style={{fontSize:"13px",color:C.muted,marginTop:"1px"}}>{subtitle}</div>
      </div>
      {!isMobile&&<div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:"8px"}}>
        <div style={{display:"flex",alignItems:"center",background:"#fff",border:`1px solid ${C.border}`,borderRadius:"8px",padding:"0 10px",gap:"6px",height:"34px"}}>
          <Icon name="search" size={13} color={C.hint}/>
          <input value={searchInput} onChange={e=>onSearch(e.target.value)} placeholder={placeholder||"Search..."}
            style={{border:"none",outline:"none",fontSize:"14px",color:C.text,background:"none",width:"160px"}}/>
        </div>
        <div style={{position:"relative",display:"flex",alignItems:"center"}}>
          <select value={sort} onChange={e=>onSort(e.target.value)}
            style={{padding:"6px 28px 6px 10px",borderRadius:"8px",border:`1px solid ${C.border}`,
              background:"#fff",color:C.muted,fontSize:"13px",outline:"none",cursor:"pointer",appearance:"none"}}>
            {SORT_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div style={{position:"absolute",right:"8px",pointerEvents:"none"}}><Icon name="chevron" size={12} color={C.hint}/></div>
        </div>
      </div>}
    </div>
  );
}

// ── Bulk action bar — Activate + Delete only (already inactive data) ──────────
function ReviewBulkBar({ selected, total, onSelectAll, onClearAll, onAction, loading, color }) {
  const count = selected.length;
  return (
    <div style={{ background:"#fff", borderBottom:`1px solid ${C.border}`,
      padding:"8px 0", display:"flex", alignItems:"center", gap:10,
      boxShadow:"0 2px 8px rgba(0,0,0,0.06)", marginBottom:12 }}>
      <label style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer",
        fontSize:13, fontWeight:600, color:C.text, userSelect:"none" }}>
        <input type="checkbox"
          checked={count > 0 && count === total}
          ref={el => { if(el) el.indeterminate = count > 0 && count < total; }}
          onChange={e => e.target.checked ? onSelectAll() : onClearAll()}
          style={{ width:15, height:15, cursor:"pointer", accentColor:color }} />
        {count === 0 ? "Select all"
          : count === total ? `All ${total} selected`
          : `${count} of ${total} selected`}
      </label>
      <div style={{ flex:1 }} />
      {count > 0 && (
        <>
          <button onClick={() => onAction(2)} disabled={loading}
            style={{ padding:"6px 14px", borderRadius:7, border:"none",
              background:"#22C55E", color:"#fff", fontSize:12, fontWeight:600,
              cursor:loading?"not-allowed":"pointer", opacity:loading?0.6:1 }}>
            ✓ Activate ({count})
          </button>
          <button onClick={() => onAction(4)} disabled={loading}
            style={{ padding:"6px 14px", borderRadius:7, border:"none",
              background:"#EF4444", color:"#fff", fontSize:12, fontWeight:600,
              cursor:loading?"not-allowed":"pointer", opacity:loading?0.6:1 }}>
            🗑 Delete ({count})
          </button>
          <button onClick={onClearAll} disabled={loading}
            style={{ padding:"6px 10px", borderRadius:7,
              border:`1px solid ${C.border}`, background:"transparent",
              color:C.muted, fontSize:12, cursor:"pointer" }}>
            ✕ Clear
          </button>
        </>
      )}
    </div>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function ReviewPublish({user, subproject}) {
  const ctx   = useContext(ThemeCtx) || {};
  const color = ctx.theme?.primary || C.primary;

  const [docs,        setDocs]       = useState([]);
  const [loading,     setLoading]    = useState(false);
  const [hasMore,     setHasMore]    = useState(true);
  const [error,       setError]      = useState(null);
  const [searchInput, setSearchInput]= useState("");
  const [search,      setSearch]     = useState("");
  const [sort,        setSort]       = useState("latest");
  const [hashtags,    setHashtags]   = useState([]);
  const [activeTag,   setActiveTag]  = useState(null);

  const pageRef      = useRef(0);
  const searchTimer  = useRef(null);
  const activeTagRef = useRef(null);
  const extraParamsRef = useRef({ timestamp: since7days() });

  const loadDocs = useCallback(async (reset=false, overrides={}) => {
    if(!subproject) return;
    setLoading(true); setError(null);
    if(reset) clearOnReset();
    const first = reset ? 0 : pageRef.current * PAGE_SIZE;
    const params = {
      subprojectid: subproject.SUBPROJECTID,
      userid:       user.USERID,
      stateid:      1,
      first, last:  PAGE_SIZE,
      sort:         overrides.sort||sort,
      ...extraParamsRef.current,
    };
    if(overrides.search!==undefined) params.search=overrides.search;
    else if(activeTagRef.current)    params.search=activeTagRef.current;
    else if(search)                  params.search=search;
    try {
      const r = await post("advance_filter", params, user.AUTH_CODE);
      if(r.STATUS==="1"&&Array.isArray(r.DATA)&&r.DATA.length>0){
        setDocs(prev=>reset?r.DATA:[...prev,...r.DATA]);
        setHasMore(r.DATA.length===PAGE_SIZE);
        pageRef.current = reset ? 1 : pageRef.current+1;
        if(reset){
          const all=new Set();
          if(Array.isArray(r.HASHTAGS)) r.HASHTAGS.forEach(t=>all.add(t));
          r.DATA.forEach(d=>(Array.isArray(d.HASHTAGS)?d.HASHTAGS:[]).forEach(t=>all.add(t)));
          if(all.size>0) setHashtags([...all].sort());
        }
      } else {
        if(reset){ setDocs([]); setHashtags([]); }
        setHasMore(false);
      }
    } catch { setError("Failed to load data."); }
    setLoading(false);
  },[subproject, user, search, sort]);

  const {
    selected, actLoading, actMsg, confirm,
    toggleSelect, selectAll, clearAll, clearOnReset,
    handleBulkAction, handleConfirmed, handleCancelled,
  } = useFilterActions({
    user, subproject, docs,
    onRefresh: () => {
      pageRef.current=0; setDocs([]); setHasMore(true);
      loadDocs(true);
    },
  });

  useEffect(()=>{
    extraParamsRef.current = { timestamp: since7days() };
    pageRef.current=0; setDocs([]); setHasMore(true);
    loadDocs(true);
  },[subproject?.SUBPROJECTID]);

  const handleSearch = val=>{
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current=setTimeout(()=>{
      setSearch(val); pageRef.current=0; setDocs([]); setHasMore(true);
      loadDocs(true,{search:val});
    },500);
  };
  const handleSort = val=>{
    setSort(val); pageRef.current=0; setDocs([]); setHasMore(true);
    loadDocs(true,{sort:val});
  };
  const handleTag = tag=>{
    activeTagRef.current=tag; setActiveTag(tag);
    pageRef.current=0; setDocs([]); setHasMore(true);
    loadDocs(true);
  };

  return(
    <div style={{padding:"20px"}}>
      <ScreenHeader title="Review & Publish"
        subtitle={`${subproject?.SUMMARY} · ${docs.length} drafts · last 7 days`}
        searchInput={searchInput} onSearch={handleSearch}
        sort={sort} onSort={handleSort}
        placeholder="Search drafts..."/>
      <ConfirmDialog confirm={confirm} onConfirm={handleConfirmed} onCancel={handleCancelled} />
      {docs.length > 0 && (
        <ReviewBulkBar
          selected={selected} total={docs.length}
          onSelectAll={selectAll} onClearAll={clearAll}
          onAction={handleBulkAction} loading={actLoading} color={color} />
      )}
      <ActionMsg msg={actMsg} />

      {error&&<div style={{padding:"10px 14px",borderRadius:"8px",fontSize:"14px",
        marginBottom:"1rem",background:C.errorBg,color:C.error}}>{error}</div>}
      {docs.length===0&&!loading&&(
        <div style={{textAlign:"center",padding:"4rem 0"}}>
          <Icon name="attach" size={40} color={C.border}/>
          <div style={{fontSize:"15px",color:C.muted,marginTop:"1rem"}}>No drafts in the last 7 days</div>
        </div>
      )}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(220px, 1fr))",gap:"12px"}}>
        {docs.map(doc=>(
          <SelectableTile key={doc.DOCUMENTID}
            doc={doc} selected={selected}
            onToggle={toggleSelect}
            onOpen={openDocTab}
            subId={subproject?.SUBPROJECTID}
            showDocType={true} />
        ))}
      </div>
      {loading&&<Spinner/>}
      {hasMore&&!loading&&docs.length>0&&<LoadMore onClick={()=>loadDocs(false)}/>}
    </div>
  );
}